import { useEffect, useState } from "react";
import { parseCompanySourceIdList } from "../utils/companyEditor";

const useCompanySources = ({
  onAddCompanySources,
  onRemoveCompanySources,
  companyRecordId,
}) => {
  const [isSourceWindowOpen, setIsSourceWindowOpen] = useState(false);
  const [sourceWindowDataset, setSourceWindowDataset] = useState("");
  const [sourceWindowInput, setSourceWindowInput] = useState("");
  const [sourceWindowError, setSourceWindowError] = useState("");
  const [sourceInlineInputByDataset, setSourceInlineInputByDataset] = useState({});
  const [sourceInlineErrorByDataset, setSourceInlineErrorByDataset] = useState({});

  useEffect(() => {
    setIsSourceWindowOpen(false);
    setSourceWindowDataset("");
    setSourceWindowInput("");
    setSourceWindowError("");
    setSourceInlineInputByDataset({});
    setSourceInlineErrorByDataset({});
  }, [companyRecordId]);

  const openSourceWindow = () => {
    setSourceWindowDataset("");
    setSourceWindowInput("");
    setSourceWindowError("");
    setIsSourceWindowOpen(true);
  };

  const closeSourceWindow = () => {
    setIsSourceWindowOpen(false);
    setSourceWindowDataset("");
    setSourceWindowInput("");
    setSourceWindowError("");
  };

  const handleSourceInlineInputChange = (dataset, value) => {
    setSourceInlineInputByDataset((current) => ({
      ...current,
      [dataset]: value,
    }));
    setSourceInlineErrorByDataset((current) => ({
      ...current,
      [dataset]: "",
    }));
  };

  const clearSourceInlineInput = (dataset) => {
    setSourceInlineInputByDataset((current) => ({
      ...current,
      [dataset]: "",
    }));
    setSourceInlineErrorByDataset((current) => ({
      ...current,
      [dataset]: "",
    }));
  };

  const submitCompanySourceIds = async ({ dataset, sourceIds, clearInput = false }) => {
    const normalizedDataset = String(dataset || "").trim();
    const normalizedSourceIds = Array.isArray(sourceIds)
      ? sourceIds.map((value) => String(value || "").trim()).filter(Boolean)
      : [];

    if (!normalizedDataset) {
      return;
    }

    if (!normalizedSourceIds.length) {
      throw new Error("Add at least one source ID.");
    }

    await onAddCompanySources?.({
      dataset: normalizedDataset,
      sourceIds: normalizedSourceIds,
      companyRecordId,
    });

    if (clearInput) {
      clearSourceInlineInput(normalizedDataset);
    }
  };

  const handleRemoveCompanySourceIds = async ({ dataset, sourceIds }) => {
    const normalizedDataset = String(dataset || "").trim();
    const normalizedSourceIds = Array.isArray(sourceIds)
      ? sourceIds.map((value) => String(value || "").trim()).filter(Boolean)
      : [];

    if (!normalizedDataset) {
      return;
    }

    if (!normalizedSourceIds.length) {
      setSourceInlineErrorByDataset((current) => ({
        ...current,
        [normalizedDataset]: "Remove at least one source ID.",
      }));
      return;
    }

    try {
      await onRemoveCompanySources?.({
        dataset: normalizedDataset,
        sourceIds: normalizedSourceIds,
        companyRecordId,
      });
      setSourceInlineErrorByDataset((current) => ({
        ...current,
        [normalizedDataset]: "",
      }));
    } catch (error) {
      setSourceInlineErrorByDataset((current) => ({
        ...current,
        [normalizedDataset]: error?.message || "Unable to remove the selected sources.",
      }));
    }
  };

  const handleSubmitCompanySources = async () => {
    const normalizedDataset = String(sourceWindowDataset || "").trim();
    const sourceIds = parseCompanySourceIdList(sourceWindowInput);

    if (!normalizedDataset) {
      setSourceWindowError("Choose a dataset.");
      return;
    }

    if (!sourceIds.length) {
      setSourceWindowError("Add at least one source ID.");
      return;
    }

    setSourceWindowError("");
    try {
      await submitCompanySourceIds({
        dataset: normalizedDataset,
        sourceIds,
      });
      closeSourceWindow();
    } catch (error) {
      setSourceWindowError(error?.message || "Unable to attach the selected sources.");
    }
  };

  return {
    clearSourceInlineInput,
    closeSourceWindow,
    handleSourceInlineInputChange,
    handleRemoveCompanySourceIds,
    handleSubmitCompanySources,
    isSourceWindowOpen,
    openSourceWindow,
    setSourceWindowDataset,
    setSourceWindowInput,
    sourceInlineErrorByDataset,
    sourceInlineInputByDataset,
    sourceWindowDataset,
    sourceWindowError,
    sourceWindowInput,
    setSourceInlineErrorByDataset,
    setSourceWindowError,
    submitCompanySourceIds,
  };
};

export default useCompanySources;
